import { useCallback, useEffect, useRef, useState } from 'react'
import { Search, Radio } from 'lucide-react'

// Minor codes that indicate a denied access — mirrors hikvision-client/src/modules/events.ts
const DENIED_MINORS = new Set([
  1, 2, 3,
  16, 22, 25, 28,
  32, 33, 34, 35, 36,
  37, 38, 39, 40, 41,
  45, 46, 47, 48, 49,
  101,
  121, 122,
])

// Complete DS-K1T321MFWX minor code labels (major=5 ACS)
const MINOR_LABELS: Record<number, string> = {
  // Access granted — authentication method
  5:   'Acceso concedido con tarjeta',
  6:   'Acceso concedido',
  7:   'Apertura de emergencia',
  8:   'Autentificado mediante Huella Digital',
  9:   'Autentificado mediante Reconocimiento Facial',
  10:  'Autentificado mediante Huella + Cara',
  11:  'Apertura con primera tarjeta',
  12:  'Acceso bajo coacción',
  13:  'Acceso con tarjeta de emergencia',
  14:  'Acceso exento de verificación de huella',
  // Access denied
  1:   'Tarjeta no existe',
  2:   'Tarjeta expirada',
  3:   'Sin permiso de acceso',
  4:   'Contraseña incorrecta',
  15:  'No registrado en el dispositivo',
  16:  'Bloqueo multi-puerta',
  22:  'Anti-passback',
  25:  'Tailgating detectado',
  28:  'Acceso denegado',
  32:  'Acceso denegado por controlador',
  33:  'Fuera de horario permitido',
  34:  'Fuera de zona de acceso',
  35:  'Denegado por lector de tarjetas',
  36:  'Sin derechos de acceso',
  37:  'Visitante no registrado',
  38:  'Fuera de tiempo de acceso',
  39:  'Fuera de plan de acceso',
  40:  'Zona horaria inválida',
  41:  'Acceso denegado',
  45:  'Huella digital inválida',
  46:  'Reconocimiento facial fallido',
  47:  'Tarjeta + huella inválida',
  48:  'Autenticación combinada fallida',
  49:  'PIN incorrecto',
  101: 'Autenticación fallida',
  121: 'Tarjeta en lista negra',
  122: 'Tarjeta expirada',
  // Door / system events
  44:  'Puerta abierta',
  51:  'Puerta cerrada',
  52:  'Puerta abierta',
  53:  'Puerta cerrada con llave',
  54:  'Puerta abierta demasiado tiempo',
  55:  'Puerta forzada',
  68:  'Puerta abierta normalmente',
  69:  'Puerta cerrada con llave',
  70:  'Botón de salida presionado',
  75:  'Desbloqueo remoto',
  76:  'Armado remoto',
  77:  'Desarmado remoto',
  78:  'Armado remoto',
  79:  'Desarmado remoto',
  80:  'Bloqueo remoto',
  200: 'Puerta abierta',
  201: 'Puerta cerrada con llave',
  202: 'Desbloqueo remoto',
  203: 'Armado remoto',
  204: 'Desarmado remoto',
}

const VERIFY_MODE_ES: Record<string, string> = {
  fingerprint: 'Huella Digital',
  fingerprintOrCard: 'Huella Digital',
  fingerOrPwd: 'Huella Digital',
  fingerPrint: 'Huella Digital',
  card: 'Tarjeta',
  cardOrPwd: 'Tarjeta',
  cardAndPwd: 'Tarjeta + Contraseña',
  face: 'Reconocimiento Facial',
  faceRecognize: 'Reconocimiento Facial',
  faceOrCard: 'Reconocimiento Facial',
  faceOrFingerprint: 'Reconocimiento Facial o Huella',
  faceAndFingerprint: 'Reconocimiento Facial + Huella',
  password: 'Contraseña',
  iris: 'Iris',
  pin: 'Contraseña',
  'face+card': 'Reconocimiento Facial + Tarjeta',
}

interface DeviceEvent {
  employeeNo: string
  name?: string
  cardNo?: string
  doorNo?: number
  verifyMode?: string
  occurredAt: string
  minor?: number
  // present only on live SSE events (no minor available)
  eventType?: 'ACCESS_GRANTED' | 'ACCESS_DENIED'
  isLive?: boolean
}

// Raw payload emitted by device-stream-manager via SSE
interface RawStreamEvent {
  type: string
  deviceId: string
  employeeNo: string | null
  name: string | null
  eventType: 'ACCESS_GRANTED' | 'ACCESS_DENIED'
  minor: number | null
  doorNo: number | null
  verifyMode: string
  occurredAt: string
}

function getEventLabel(ev: DeviceEvent): string {
  const minor = ev.minor ?? -1

  if (ev.employeeNo) {
    const denied = minor >= 0 ? DENIED_MINORS.has(minor) : ev.eventType === 'ACCESS_DENIED'
    if (denied) return MINOR_LABELS[minor] ?? 'Acceso denegado'
    if (minor >= 0 && MINOR_LABELS[minor]) return MINOR_LABELS[minor]
    const method = VERIFY_MODE_ES[ev.verifyMode ?? ''] ?? ''
    return method ? `Autentificado mediante ${method}` : 'Acceso concedido'
  }

  if (minor >= 0) return MINOR_LABELS[minor] ?? 'Evento del sistema'
  if (ev.eventType === 'ACCESS_DENIED') return 'Acceso denegado'
  return 'Evento del sistema'
}

export interface DeviceEventsTabProps {
  deviceId: string
  proxyUrl: string
  proxyToken: string
}

export function DeviceEventsTab({ deviceId, proxyUrl, proxyToken }: DeviceEventsTabProps) {
  const [allEvents, setAllEvents] = useState<DeviceEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pageNo, setPageNo] = useState(1)
  const [liveConnected, setLiveConnected] = useState(false)
  const PAGE_SIZE = 10

  const esRef = useRef<EventSource | null>(null)

  // SSE — real-time events from device stream
  useEffect(() => {
    // EventSource API does not support custom headers. The proxyToken is passed as a
    // query parameter — risk is mitigated by the token being regenerated at every
    // Electron startup (short-lived, session-scoped).
    const url = `${proxyUrl}/stream?devices=${deviceId}&proxyToken=${proxyToken}`
    const es = new EventSource(url)
    esRef.current = es

    es.onopen = () => setLiveConnected(true)

    es.onmessage = (e) => {
      try {
        const raw = JSON.parse(e.data as string) as RawStreamEvent
        if (raw.type !== 'accessEvent') return
        const liveEvent: DeviceEvent = {
          employeeNo: raw.employeeNo ?? '',
          name: raw.name ?? undefined,
          doorNo: raw.doorNo ?? undefined,
          verifyMode: raw.verifyMode,
          occurredAt: raw.occurredAt,
          eventType: raw.eventType,
          minor: raw.minor ?? undefined,
          isLive: true,
        }
        setAllEvents(prev => [liveEvent, ...prev])
        setPageNo(1)
      } catch { /* ignore malformed */ }
    }

    es.onerror = () => setLiveConnected(false)

    return () => {
      es.close()
      esRef.current = null
      setLiveConnected(false)
    }
  }, [proxyUrl, proxyToken, deviceId])

  const fetchEvents = useCallback(() => {
    setLoading(true)
    setError(null)
    fetch(`${proxyUrl}/events/${deviceId}`, {
      headers: { Authorization: `Bearer ${proxyToken}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Error ${r.status}`)
        return r.json() as Promise<{ events: DeviceEvent[] }>
      })
      .then((data) =>
        setAllEvents(
          [...data.events].sort(
            (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
          ),
        ),
      )
      .catch(() => setError('No se pudo conectar con el dispositivo'))
      .finally(() => setLoading(false))
  }, [proxyUrl, proxyToken, deviceId])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  // Auto-refresh cada 30s
  useEffect(() => {
    const id = setInterval(fetchEvents, 30_000)
    return () => clearInterval(id)
  }, [fetchEvents])

  const totalPages = Math.max(1, Math.ceil(allEvents.length / PAGE_SIZE))
  const pageEvents = allEvents.slice((pageNo - 1) * PAGE_SIZE, pageNo * PAGE_SIZE)

  return (
    <div className="flex flex-col gap-3">
      {/* Header — live indicator + refresh */}
      <div className="flex items-center justify-between">
        <span className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${
          liveConnected
            ? 'text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10'
            : 'text-text-muted border-border'
        }`}>
          <Radio className="w-3 h-3" />
          {liveConnected ? 'En vivo' : 'Reconectando...'}
        </span>

        <button
          onClick={() => { setPageNo(1); fetchEvents() }}
          disabled={loading}
          className="flex items-center gap-1.5 border border-border text-text-muted text-xs px-3 py-1.5 rounded-md hover:text-text-primary transition-colors disabled:opacity-40"
        >
          <Search className="w-3 h-3" />
          Actualizar
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-text-muted text-sm py-4">Cargando eventos...</p>
      ) : error ? (
        <p className="text-red-600 dark:text-red-400 text-sm py-4">{error}</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2.5 text-text-muted text-xs">ID Empleado</th>
                  <th className="text-left px-4 py-2.5 text-text-muted text-xs">Nombre</th>
                  <th className="text-left px-4 py-2.5 text-text-muted text-xs">Nº Tarjeta</th>
                  <th className="text-left px-4 py-2.5 text-text-muted text-xs">Tipo de Evento</th>
                  <th className="text-left px-4 py-2.5 text-text-muted text-xs">Tiempo</th>
                  <th className="text-left px-4 py-2.5 text-text-muted text-xs">Operación</th>
                </tr>
              </thead>
              <tbody>
                {pageEvents.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-text-muted text-xs">
                      Sin eventos registrados
                    </td>
                  </tr>
                )}
                {pageEvents.map((ev, i) => {
                  const minor = ev.minor ?? -1
                  const denied = minor >= 0 ? DENIED_MINORS.has(minor) : ev.eventType === 'ACCESS_DENIED'
                  const label = getEventLabel(ev)
                  return (
                    <tr
                      key={i}
                      className={`border-b border-border last:border-0 transition-colors ${
                        ev.isLive ? 'bg-accent/5 hover:bg-accent/10' : 'hover:bg-white/5'
                      }`}
                    >
                      <td className="px-4 py-3 text-text-primary text-xs">
                        {ev.employeeNo || '—'}
                        {ev.isLive && <span className="ml-1.5 text-[9px] text-accent font-bold">NUEVO</span>}
                      </td>
                      <td className="px-4 py-3 text-text-primary text-xs">{ev.name ?? '—'}</td>
                      <td className="px-4 py-3 text-text-muted text-xs">{ev.cardNo ?? '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className={ev.employeeNo && denied ? 'text-red-600 dark:text-red-400' : ev.employeeNo ? 'text-emerald-600 dark:text-emerald-400' : 'text-text-muted'}>
                          {label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-muted text-xs whitespace-nowrap">
                        {new Date(ev.occurredAt).toLocaleString('es-CO')}
                      </td>
                      <td className="px-4 py-3 text-text-muted text-xs">
                        {VERIFY_MODE_ES[ev.verifyMode ?? ''] || '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 items-center justify-end">
            <span className="text-text-muted text-xs">
              Página {pageNo} de {totalPages} ({allEvents.length} registros)
            </span>
            <button
              onClick={() => setPageNo(p => p - 1)}
              disabled={pageNo === 1}
              className="border border-border text-text-muted text-xs px-3 py-1.5 rounded-md disabled:opacity-40 hover:text-text-primary transition-colors"
            >
              ← Anterior
            </button>
            <button
              onClick={() => setPageNo(p => p + 1)}
              disabled={pageNo >= totalPages}
              className="border border-border text-text-muted text-xs px-3 py-1.5 rounded-md disabled:opacity-40 hover:text-text-primary transition-colors"
            >
              Siguiente →
            </button>
          </div>
        </>
      )}
    </div>
  )
}
